import { SelectOptionItem } from './common'
import { Historical } from './historical'

import { TriggerConditionItem } from '@features/operations/components/pages/Programs/ProgramView/constant'
import { TurfQuality } from './turfQuality'
import { STATION_DELAY_ID_DEFAULT } from './station'

export enum StrategyMode {
  Auto = 0,
  Sequential = 1,
}

export interface CreateProgram {
  name: string
  active: boolean
  syncToMobileApp: boolean
  isMustFinished: boolean
  autoChangeWaterBudget: boolean
  programMode: string
  typeOfMode: string
  oddEvenType: string
  weekDays: number[]
  amount?: string
  waterRequired: number
  landscapeArea: number
  waterAllocation: number
  etReferenceId: number
  allocationPercent: number | null
  fertigation: boolean
  filtration: boolean
  cycleAndSoak: boolean
  cycle: number
  soak: number
  waterBudget: number
  wateringTime: {
    active: boolean
    fromMonth: number
    fromDay: number
    toMonth: number
    toDay: number
    fromHour: number
    fromMinute: number
    toHour: number
    toMinute: number
    repeatCount?: number
    repeatInterval?: number
    isWholeYearPeriod: boolean
  }[]
  exclusionTime: {
    active: boolean
    fromMonth: number
    fromDay: number
    toMonth: number
    toDay: number
    fromHour: number
    fromMinute: number
    toHour: number
    toMinute: number
    repeatCount: number
    repeatInterval: number
    weekDays: number[]
    oddEvenType: string
    isGlobal: boolean
    isWholeYearPeriod: boolean
  }[]
  interfaceId: number
  stationAmounts: {
    stationId: number
    amount?: string
    isCheck: boolean
    isCustom: boolean
    waitCompletion?: boolean
    order?: number
    pipeId?: number
  }[]
  triggerConditions: [
    {
      id?: number
      trigger: number
      waterRequired: number
    },
    {
      id?: number
      trigger: number
      waterRequired: number
    },
  ]
  fertigations: number[]

  cisFertigations: ICisFertigationItemForm[]
  turfQualityId: number
  strategy: StrategyMode
  evapotranspiration?: number
  weatherStationId?: string
  customRules?: ICustomRule[]
}

export interface UpdateGlobalExclusion {
  oddEvenType: string
  weekDays: number[]
  active: boolean
  fromMonth: number
  fromDay: number
  toMonth: number
  toDay: number
  fromHour: number
  fromMinute: number
  toHour: number
  toMinute: number
  repeatCount: number
  repeatInterval: number
  interfaceId: number
  id: number
  isWholeYearPeriod?: boolean
}

export interface UpdateProgram {
  id?: number
  name: string
  active: boolean
  syncToMobileApp: boolean
  isMustFinished: boolean
  autoChangeWaterBudget: boolean
  programMode: string
  typeOfMode: string
  oddEvenType: string
  weekDays: number[]
  amount?: string
  waterRequired: number
  landscapeArea: number
  waterAllocation: number
  waterBudget: number
  etReferenceId: number
  allocationPercent: number | null
  fertigation: boolean
  filtration: boolean
  cycleAndSoak: boolean
  cycle: number
  soak: number
  wateringTime: {
    id?: number
    active: boolean
    fromMonth?: number
    fromDay?: number
    toMonth?: number
    toDay?: number
    fromHour?: number
    fromMinute?: number
    toHour?: number
    toMinute?: number
    repeatCount?: number
    repeatInterval?: number
    isWholeYearPeriod?: boolean
  }[]
  exclusionTime: {
    id?: number
    active: boolean
    fromMonth?: number
    fromDay?: number
    toMonth?: number
    toDay?: number
    fromHour?: number
    fromMinute?: number
    toHour?: number
    toMinute?: number
    repeatCount?: number
    repeatInterval?: number
    weekDays?: number[]
    oddEvenType?: string
    isGlobal?: boolean
    isWholeYearPeriod?: boolean
  }[]
  interfaceId: number
  stationAmounts: {
    stationId: number
    amount?: string
    isCheck: boolean
    isCustom: boolean
    waitCompletion?: boolean
    pipeId?: number
    order?: number
  }[]
  triggerConditions: [
    {
      id?: number
      trigger: number
      waterRequired: number
    },
    {
      id?: number
      trigger: number
      waterRequired: number
    },
  ]
  fertigations: number[]

  cisFertigations: ICisFertigationItemForm[]
  turfQualityId: number
  strategy: StrategyMode
  evapotranspiration?: number
  weatherStationId?: string | null
  customRules?: ICustomRule[]
}

export interface RunTime {
  programId?: number
  interfaceId: number
  stationIds: number[]
  etReferenceId: number
  programMode: string
  typeOfMode: string
  customRunTime: string
  customWaterRequired: number
  landscapeArea: number
  waterAllocation: number
  waterBudget: number
  allocPercent: number | null
  evapotranspiration?: number
  weatherStationId?: string | null
  triggerConditions: TriggerConditionItem[]
  customRules?: ICustomRule[]
}

export interface AutoUpdateWaterBudget {
  wateringTime: {
    active: boolean
    fromMonth: number
    fromDay: number
    toMonth: number
    toDay: number
    fromHour: number
    fromMinute: number
    toHour: number
    toMinute: number
    repeatCount: number
    repeatInterval: number
  }
  stationRuntime: {
    interfaceId: number
    stationIds: number[]
    etReferenceId: number
    programMode: string
    typeOfMode: string
    customRunTime: string
    customWaterRequired: number
    landscapeArea: number
    waterAllocation: number
    allocPercent: number
    waterBudget: number
    triggerConditions: {
      trigger: number
      waterRequired: number
    }[]
  }
}

export interface LocationStationFiltered {
  id: number
  programId: number[]
  controller: string
  locationId: number
  interfaceId: number
  isPicked: boolean
  etZoneId: number | null
  amount?: number
  stepId?: number
  plantId?: number
  emitterId?: number
  soilId?: number
  pipeId?: number
}

export interface AreaSubFiltered {
  id: number
  controller: string
  locationId: number
  interfaceId: number
  isPicked: boolean
}

export interface LocationFiltered {
  id: number
  interfaceId: number
  name: string
  siteId: number
  siteName: string
  stations: LocationStationFiltered[]
  subAreaId: number
  subAreaName: string
  areaName: string
  areaId: number
}

export const ProgramMode = {
  RunCisgenET: 'Run-Cisgen.ET',
  RunMM: 'Run-Mm',
  RunTime: 'Run-Time',
  RunSoilMoisture: 'Run-Soil Moisture',
  WaterAlloc: 'Water Alloc',
  LearnFlow: 'LearnFlow',
  VerifyFlow: 'VerifyFlow',
  Evapotranspiration: 'Evapotranspiration',
  Custom: 'Custom',
}

export const EvapotranspirationMode = {
  ETWeatherStation: 10,
  ETReference: 1,
}

export const ProgramModeTransKey = {
  [ProgramMode.RunCisgenET]: 'runCisgenet',
  [ProgramMode.RunMM]: 'programRunMm',
  [ProgramMode.RunTime]: 'runTime',
  [ProgramMode.Evapotranspiration]: 'programs.forms.evapotranspiration',
  [ProgramMode.RunSoilMoisture]: 'runSoilMoisture',
  [ProgramMode.WaterAlloc]: 'waterAllocation',
  [ProgramMode.LearnFlow]: 'learnFlow',
  [ProgramMode.VerifyFlow]: 'verifyFlow',
  [ProgramMode.Custom]: 'custom',
}

export const programModes = [
  { value: ProgramMode.RunCisgenET, label: 'Run - Cisgen.ET' },
  { value: ProgramMode.RunMM, label: `Run - mm` },
  { value: ProgramMode.RunTime, label: 'Run - Time' },
  { value: ProgramMode.RunSoilMoisture, label: 'Run - Soil Moisture' },
  { value: ProgramMode.WaterAlloc, label: 'Water Allocation' },
  { value: ProgramMode.LearnFlow, label: 'Learn Flow ' },
  { value: ProgramMode.VerifyFlow, label: 'Verify Flow' },
  { value: ProgramMode.Custom, label: 'Custom' },
]

export enum ConditionOperator {
  LessThan = '<',
  LessThanOrEqual = '<=',
  Equal = '==',
  GreaterThanOrEqual = '>=',
  GreaterThan = '>',
  Between = 'between',
}

export interface IConditionLeaf {
  id?: string
  type: 'leaf'
  dataDevice: { id: number | string; label: string } | null
  dataKey: { id: string; label: string } | null
  operator: ConditionOperator | string
  value?: number | string
  lowValue?: number | string
  highValue?: number | string
}

export interface IConditionGroup {
  id?: string
  type: 'group'
  operator: 'AND' | 'OR'
  children: (IConditionGroup | IConditionLeaf)[]
}

export enum CustomRuleIrrigationMode {
  RunTime = 'Run-Time',
  RunMm = 'Run-mm',
  Evapotranspiration = 'Evapotranspiration',
}

export interface ICustomRule {
  id: string
  condition: IConditionGroup
  irrigationMode: CustomRuleIrrigationMode
  amount?: number | string
  weatherStationId?: number | string | { id: string | number; description?: string } | null
}

export enum ProgramStateKey {
  Idling = 1,
  Running = 2,
  Manual = 3,
}

export const ProgramState = {
  [ProgramStateKey.Idling]: 'Idling',
  [ProgramStateKey.Running]: 'Running',
  [ProgramStateKey.Manual]: 'Manual',
}

export const modeIrrigation = {
  Manual: 1,
  Auto: 2,
  Paused: 3,
}

export const dashboardModes = [
  { value: modeIrrigation.Manual, label: 'Manual' },
  { value: modeIrrigation.Auto, label: 'Auto' },
  { value: modeIrrigation.Paused, label: 'Paused' },
]

// Runtime
export const RunTimeMode = {
  DefaultRuntime: 'Default Runtime',
  CustomRuntime: 'Custom Runtime',
}

export const runTimeModes = [
  { value: RunTimeMode.DefaultRuntime, label: 'Default Runtime' },
  { value: RunTimeMode.CustomRuntime, label: 'Custom Runtime' },
]

// RunMm
export const RunMmMode = {
  DefaultWaterAmount: 'DefaultRequiredMmIn',
  CustomWaterAmount: 'CustomRequiredMmIn',
}

export enum AgitatorMode {
  None = 0,
  PreAgitationOnly = 10,
  Continuous = 20,
  Intermittent = 30,
}

export enum FlushingMode {
  None = 0,
  Series = 10,
  Parallel = 20,
}

export interface GetStationsBasedOnLocationParams {
  Keyword?: string
  SortBy?: string
  SortDirection?: string
  PageSize?: number
  PageIndex?: number
  Offset?: number
  locationIds?: number[]
  subAreaIds?: number[]
  plantIds?: number[]
  soilIds?: number[]
  emitterIds?: number[]
  locationId?: number
  isReload?: boolean
  stationGroupId?: number
  isRequiredEtZone?: boolean
}

export enum DayType {
  Any = 'Any',
  Odd = 'Odd',
  Even = 'Even',
  OddNew = 'OddLastExcl',
}

export const dayTypes: SelectOptionItem[] = [
  { value: DayType.Any, label: 'Any' },
  { value: DayType.Even, label: 'Even' },
  { value: DayType.Odd, label: 'Odd' },
  { value: DayType.OddNew, label: 'Odd but not last day' },
]

export const daysList: SelectOptionItem[] = [
  { value: 2, label: 'M', type: DayType.Even, label2: 'Mon' },
  { value: 3, label: 'T', type: DayType.Odd, label2: 'Tue' },
  { value: 4, label: 'W', type: DayType.Even, label2: 'Wed' },
  { value: 5, label: 'T', type: DayType.Odd, label2: 'Thu' },
  { value: 6, label: 'F', type: DayType.Even, label2: 'Fri' },
  { value: 7, label: 'Sa', type: DayType.Any, label2: 'Sat' },
  { value: 8, label: 'Su', type: DayType.Even, label2: 'Sun' },
]

export const daysListExclusion: SelectOptionItem[] = [
  { value: 2, label: 'M', type: DayType.Even, label2: 'Mon' },
  { value: 3, label: 'T', type: DayType.Odd, label2: 'Tue' },
  { value: 4, label: 'W', type: DayType.Even, label2: 'Wed' },
  { value: 5, label: 'T', type: DayType.Odd, label2: 'Thu' },
  { value: 6, label: 'F', type: DayType.Even, label2: 'Fri' },
  { value: 7, label: 'Sa', type: DayType.Any, label2: 'Sat' },
  { value: 8, label: 'Su', type: DayType.Even, label2: 'Sun' },
]

export interface ProgramRunHistory {
  startTime: string
  endTime: string
  historyET: number | null
  waterConsumption: number
}

export interface ICisFertigationItem {
  id?: string
  cisFertigationId: string | null
  agitationMode: AgitatorMode | null
  flushingMode: number | null
  preIrrigation: number | null
  postIrrigation: number | null
  minutesRunBefore: number
  onDuration: number
  offDuration: number
  duration: number
  name: string | null
  fertigationMode: string | null
  mainLineName: string | null
  pipeId: string | null
  isFertilizerMode?: number | null
}

export interface ICisFertigationItemForm extends ICisFertigationItem {
  isFlushingMode?: boolean
  isAgitationMode?: boolean
}

export interface Program {
  id: number
  name: string
  programMode: string
  enabled: boolean
  run: boolean
  runTime: number
  typeOfMode: string
  waterBudget: number
  typeOfModeMm: string
  fertigation: boolean
  autoChangeWaterBudget: boolean
  isMustFinished: boolean
  filtration: boolean
  cycleAndSoak: boolean
  cycle: number
  soak: number
  interfaceId: number
  waterRequired: number
  landscapeArea: number
  waterAllocation: number
  allocationPercent: number | null
  etReferenceId: Historical
  etReference: Historical
  isFirstRun: boolean
  stations: {
    amount: number
    id: number
    controller: string
    defaultRunTime: string
    name: string
    defaultWaterRequired: number
    waterConsumption: number
    isCheck: boolean
    isCustom: boolean
    waitCompletion?: boolean
    order?: number
    pipeId: number
  }[]
  oddEven: string
  weekDays: number[]
  state: string
  wateringTime: {
    active: boolean
    fromMonth: number
    fromDay: number
    toMonth: number
    toDay: number
    fromHour: number
    fromMinute: number
    toHour: number
    toMinute: number
    repeatCount: number
    repeatInterval: number
    isWholeYearPeriod: boolean
  }[]
  exclusionTime: {
    active: boolean
    fromMonth: number
    fromDay: number
    toMonth: number
    toDay: number
    fromHour: number
    fromMinute: number
    toHour: number
    toMinute: number
    repeatCount: number
    repeatInterval: number
    weekDays?: number[]
    oddEvenType?: string
    isGlobal: boolean
    isWholeYearPeriod: boolean
  }[]
  triggerConditions: [
    {
      id: number
      trigger: number
      waterRequired: number
    },
    {
      id: number
      trigger: number
      waterRequired: number
    },
  ]
  fertigations: {
    id: number
    name: string
    controllerName: string
  }[]
  cisFertigations: ICisFertigationItem[]
  strategy: StrategyMode
  autoChangeWaterBudgetOnTurfQuality: boolean
  turfQualityId: number
  syncToMobileApp: boolean
  evapotranspiration?: number
  weatherStationId?: string | null
  customRules?: ICustomRule[]
}

export interface UpdateProgramRun {
  id: number
  interfaceId: number
  status: boolean
}

export interface UpdateProgramEnable {
  id: number
  interfaceId: number
  status: boolean
}

export interface UpdateProgramStateSignalR {
  id: number
  interfaceId: number
  state: number
}

export const StationDelayDefault = {
  stationId: STATION_DELAY_ID_DEFAULT,
  amount: '00:05:00',
  isCheck: true,
  waitCompletion: false,
}

export interface IPayloadFetchET24h {
  programId?: number
  interfaceId: number
  stationIds: number[]
  etReferenceId: number
  programMode: string
  typeOfMode: string
  customRunTime: string
  customWaterRequired: number
  landscapeArea: number
  waterAllocation: number
  waterBudget: number
  allocPercent: number | null
  evapotranspiration?: number
  weatherStationId?: string | null
  triggerConditions: TriggerConditionItem[]
  customRules?: ICustomRule[]
}
